import React from 'react';
import { useSelector } from 'react-redux';
import classnames from 'classnames';

import { sendRudderstackEvent } from 'dpl/util/analytics';
import {
  ANALYTICS_EVENTS,
  ANALYTICS_VIEW_FEATURE_LOCATIONS,
  ANALYTICS_VIEW_FEATURES,
  ANALYTICS_VIEWS
} from 'dpl/constants/analytics';
import useResourceQuery from 'dpl/shared/fetching/hooks/useResourceQuery';
import { FILTER_POSTAL_CODE } from 'dpl/constants/query_params';
import { removeEmptyValues } from 'dpl/shared/utils/object';
import Icon from 'dpl/common/components/Icon';
import { BUTTON_SIZES, BUTTON_VARIANTS } from 'dpl/common/design_system/Button';
import SmartLink from 'dpl/components/SmartLink';
import UserAvatar from 'dpl/path_to_payment/components/UserAvatar';
import PreferredBreederBadge from 'dpl/breeder_preferred_qualifications/components/PreferredBreederBadge';
import { STATUS } from 'dpl/breeder_preferred_qualifications/utils/constants';
import HorizontalSlider from 'dpl/components/HorizontalSlider';

import { NearbyBreederProfilesResource } from '../constants/resource_types';
import { nearbyBreederProfilesResourceDefinition } from '../utils/queryDefinitions';

function NearbyBreederCardItem({
  iconName,
  description
}: {
  iconName: string;
  description: string;
}) {
  return (
    <div className="flex items-center secondary">
      <Icon name={iconName} className="mr2" width="24px" height="24px" />
      <p className="lh-copy">{description}</p>
    </div>
  );
}

function NearbyBreederCard({
  breeder,
  position
}: {
  breeder: any;
  position: number;
}) {
  const isPreferredBreeder =
    breeder.preferred_qualification?.status === STATUS.PREFERRED;

  function handleClick() {
    sendRudderstackEvent(ANALYTICS_EVENTS.CARD_CLICKED_BY_USER, {
      view: ANALYTICS_VIEWS.PUPPY_SEARCH_RESULTS,
      view_feature: ANALYTICS_VIEW_FEATURES.BREEDERS_NEAR_YOU_MODULE,
      view_feature_location: ANALYTICS_VIEW_FEATURE_LOCATIONS.SEARCH_RESULTS,
      page_number: 1,
      position
    });
  }

  return (
    <div className="NearbyBreederCard flex flex-column bg-white br4 overflow-hidden">
      <div className="flex items-center bg-sand pv4 ph4">
        <div className="dib relative mr3">
          <UserAvatar
            size="lg"
            user={{
              id: breeder.user_id,
              initials: breeder.initials,
              profile_photo_url:
                breeder.cropped_photo_url || breeder.profile_photo_url
            }}
          />
          {isPreferredBreeder && (
            <PreferredBreederBadge
              className="absolute right-0 bottom-0"
              width="28px"
              height="32px"
              hideTooltip
            />
          )}
        </div>
        <h6 className="NearbyBreederCard__breederName font-18 md:font-20">
          {breeder.display_name} of {breeder.name}
        </h6>
      </div>
      <div className="flex flex-column flex-grow-1 pv4 ph4 font-16">
        <div className="flex flex-column mb4 gap-12">
          <NearbyBreederCardItem
            iconName="fetch-location-house"
            description={`Raises puppies in ${breeder.facility_location}`}
          />
          <NearbyBreederCardItem
            iconName="fetch-heart-paw"
            description={breeder.availability_description}
          />
          {breeder.response_time_description && (
            <NearbyBreederCardItem
              iconName="fetch-clock"
              description={breeder.response_time_description}
            />
          )}
        </div>
        <SmartLink
          className={classnames(
            'db mt-auto',
            BUTTON_SIZES.default,
            BUTTON_VARIANTS.outline
          )}
          onClick={handleClick}
          target="_blank"
          to={breeder.url}
        >
          View profile
        </SmartLink>
      </div>
    </div>
  );
}

export default function NearbyBreedersModule() {
  const {
    breedSlug,
    citySlug,
    preappliedFilters: { stateSlug, cityName } = {}
  } = useSelector(({ server }: any) => server);
  const params = useSelector(({ queryParams }: any) => queryParams);

  const hasLocationSet = Boolean(
    cityName || citySlug || params[FILTER_POSTAL_CODE] || stateSlug
  );

  const nearbyBreedersQuery = useResourceQuery<NearbyBreederProfilesResource>({
    ...nearbyBreederProfilesResourceDefinition(
      breedSlug,
      removeEmptyValues({
        city: cityName,
        city_slug: citySlug,
        postal_code: params[FILTER_POSTAL_CODE],
        state: stateSlug
      })
    ),
    enabled: hasLocationSet
  });

  const nearbyBreeders = nearbyBreedersQuery.value.data;

  if (nearbyBreedersQuery.status === 'success' && nearbyBreeders.length > 0) {
    return (
      <li
        className={classnames(
          'NearbyBreedersModule mb8 mb10-md pv12 ph3 bg-forest-dark-900 overflow-hidden',
          {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            NearbyBreedersModule__sliderContainer: nearbyBreeders.length > 1,
            'nl4 nr4 ph7': nearbyBreeders.length === 1
          }
        )}
      >
        {nearbyBreeders.length > 1 ? (
          <div className="col-12">
            <h4 className="font-24 md:font-32 stroll-light w-80 tc center mb6">
              Connect with breeders near you
            </h4>
            <HorizontalSlider
              className="NearbyBreedersModuleSlider"
              containerClassName="NearbyBreedersModuleSlider__container flex flex-nowrap"
            >
              {nearbyBreeders.map((breeder: any, idx: number) => (
                <div key={idx} className="flex ws-normal">
                  <NearbyBreederCard breeder={breeder} position={idx + 1} />
                </div>
              ))}
            </HorizontalSlider>
          </div>
        ) : (
          <>
            <h4 className="font-24 md:font-32 stroll-light w-80 tc center mb6">
              Connect with a breeder near you
            </h4>
            <NearbyBreederCard breeder={nearbyBreeders[0]} position={1} />
          </>
        )}
      </li>
    );
  }

  return null;
}
